import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'
import type { PaginationMeta } from '../types/models.js'

export type PlatformRole = 'SUPERADMIN' | 'ADMIN' | 'MANAGER'

export const PLATFORM_ROLES: PlatformRole[] = ['SUPERADMIN', 'ADMIN', 'MANAGER']

export interface PlatformMembership {
  id: string
  userId: string
  email: string
  firstName: string | null
  lastName: string | null
  role: PlatformRole
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface PlatformMembershipListQuery {
  page?: number
  pageSize?: number
  search?: string
  role?: PlatformRole
}

export interface PlatformMembershipListResult {
  data: PlatformMembership[]
  meta: PaginationMeta
}

function token(): string | null {
  return localStorage.getItem('mf_access')
}

function authHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = token()
  if (t) headers['Authorization'] = `Bearer ${t}`
  return headers
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try {
    body = await res.json()
  } catch {
    // non-JSON error response
  }
  const errBody = (body as { error?: { code: string; message: string } } | null)?.error
    ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, errBody)
}

async function request<T>(path: string, init: RequestInit, fallback: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, fallback)
  if (res.status === 204) return null as T
  return res.json() as Promise<T>
}

export async function listPlatformMemberships(
  query: PlatformMembershipListQuery = {},
): Promise<PlatformMembershipListResult> {
  const params = new URLSearchParams()
  if (query.page) params.set('page', String(query.page))
  if (query.pageSize) params.set('pageSize', String(query.pageSize))
  if (query.search) params.set('search', query.search)
  if (query.role) params.set('role', query.role)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  return request<PlatformMembershipListResult>(
    `/platform/memberships${suffix}`,
    { method: 'GET' },
    'Failed to load platform administrators',
  )
}

export async function createPlatformMembership(input: {
  userId: string
  role: PlatformRole
}): Promise<PlatformMembership> {
  const body = await request<{ data: PlatformMembership }>(
    '/platform/memberships',
    { method: 'POST', body: JSON.stringify(input) },
    'Failed to grant platform role',
  )
  return body.data
}

export async function updatePlatformMembership(
  userId: string,
  input: { role: PlatformRole },
): Promise<PlatformMembership> {
  const body = await request<{ data: PlatformMembership }>(
    `/platform/memberships/${encodeURIComponent(userId)}`,
    { method: 'PATCH', body: JSON.stringify(input) },
    'Failed to change platform role',
  )
  return body.data
}

export async function deletePlatformMembership(userId: string): Promise<void> {
  await request<null>(
    `/platform/memberships/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
    'Failed to revoke platform role',
  )
}
